import React from 'react';
import styled from 'styled-components';
import { EmailProfileMask, ProfileMask } from '../../../../stores/types/masks';
import classNames from 'classnames';
import useDragItem from '../../../../hooks/use-dnd/use-drag-item';
import { DRAG_TYPE } from '../../../../stores/reducers/user-interface';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import TooltipWrapper from '../../../general-components/tooltips/tooltip-wrapper';

const Card = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	
	width: 213px;
	height: 120px;
	
	overflow: hidden;
	border: 1px solid #3C3A42;
	border-radius: 8px;
	
	.show-on-hover {
		opacity: 0;
		transition: opacity 0.2s;
	}

	&:hover {
		.show-on-hover {
			opacity: 1;
		}

		.shrink-on-hover {
			transform: scale(1.4) translate(-17%, 17%);
		}
	}
`;

const CardPlaceholder = styled.div`
	display: flex;
	min-width: 213px;
	min-height: 120px;
	overflow: hidden;
	border: 1px solid #3C3A42;
	border-radius: 8px;
	display: flex;
`;

const Bubble = styled.div`
	transition: transform 0.2s;

	border-radius: 50%;
	background-color: #fff;
	width: 50px;
	height: 50px;
	max-width: 50px;
	max-height: 50px;
	position: absolute;
	top: 4px;
	right: 4px;
`;

const TextRow = styled.div`
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: pre;
	width: 100%;
	background: linear-gradient(90deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
	padding: 2px 12px 2px 4px;
`;

interface Props {
	mask?: ProfileMask;
	activeOn?: string;
	canExpand?: boolean;
	deleteMask?: (id: number) => void;
	detachMask?: (email: string) => void;
}

export default function ProfileCard({ 
	mask,
	activeOn,
	canExpand,
	deleteMask,
	detachMask
}: Props) {
	const { dragRef, isDragging } = useDragItem({ 
		id: mask?.id ?? 0,
		type: DRAG_TYPE.PROFILE_MASK
	});
	const handleDelete = ({maskId,email}:{maskId: number; email: string;}) => {
		if (maskId && deleteMask) {
			deleteMask(maskId);
		} else if (email && detachMask) {
			detachMask(email);
		}
	};
	if (!mask) return <></>
	return (<div>
		{isDragging && (<CardPlaceholder />)}
		{!isDragging && (<Card 
			className={classNames({ 'can-expand': canExpand })}
			ref={dragRef}
			style={{
				backgroundImage: mask.bg_image ? `url(${mask.bg_image})` : 'none',
				backgroundSize: 'cover',
				backgroundPosition: 'center',
			}}
		>
			<div style={{position: 'absolute', bottom: 0, width: '100%'}}>
				{mask.id ? <TextRow className='show-on-hover'>
					id {mask.id}
				</TextRow> : null}
				{mask.location ? <TextRow className='show-on-hover'>
					{mask.location}
				</TextRow> : null}
				{mask.title ? <TextRow className='show-on-hover'>
					{mask.title}
				</TextRow> : null}
				{(mask.first_name || mask.last_name) 
					? <TextRow className={activeOn ? 'show-on-hover' : ''}>{mask.first_name} {mask.last_name}</TextRow>
					: null}
				{activeOn ? <TextRow className={activeOn ? '' : 'show-on-hover'}>
					{activeOn}
				</TextRow> : null}
			</div>
			{(deleteMask || detachMask) ? <button
				className='button-background-shading show-on-hover' 
				onClick={() => handleDelete({ maskId: mask.id, email: activeOn || '' })}
				style={{
					cursor: 'pointer', position: 'absolute', bottom: '-6px', right: '2px',
					width: '24px', height: '24px', borderRadius: '50%', border: 'none'
				}}
				>
				<TooltipWrapper message={`${deleteMask ? 'Delete' : 'Detach'} Mask`} position='below'>
				<Icon name={ICONS.TRASH} size={14} color={COLORS.TEXT_GRAY_500} />
				</TooltipWrapper>
			</button> : null}
			{mask.avatar ? <Bubble style={{
				borderRadius: '50%',
				backgroundImage: mask.avatar ? `url(${mask.avatar})` : 'none',
				backgroundSize: 'cover',
				backgroundPosition: 'center',
			}} className='shrink-on-hover'/> : null}
			
		</Card>)}
	</div>);
}